import React from 'react';
import { Box, BoxProps, alpha, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

const MotionBox = motion.create(Box);

interface GlassCardProps extends Omit<BoxProps, 'onAnimationStart' | 'onDragStart' | 'onDrag' | 'onDragEnd'> {
  blur?: number;
  gradient?: boolean;
  hover?: boolean;
  delay?: number;
}

export const GlassCard: React.FC<GlassCardProps> = ({ 
  children, 
  blur = 20,
  gradient = false,
  hover = true,
  delay = 0,
  sx,
  ...props 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5, 
        delay,
        ease: [0.4, 0, 0.2, 1]
      }}
      whileHover={hover ? { 
        y: -4,
        transition: { duration: 0.2 }
      } : undefined}
      sx={{
        borderRadius: 3,
        p: 3,
        background: gradient 
          ? isDark 
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.secondary.dark, 0.1)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.03)} 0%, ${alpha(theme.palette.secondary.light, 0.03)} 100%)`
          : isDark
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
        backdropFilter: isDark ? `blur(${blur}px)` : 'none',
        border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.divider, 0.8)}`,
        boxShadow: isDark 
          ? '0 8px 32px rgba(0, 0, 0, 0.4)'
          : '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': hover ? {
          boxShadow: isDark
            ? `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}`
            : `0 20px 25px ${alpha(theme.palette.primary.main, 0.12)}, 0 10px 10px rgba(0, 0, 0, 0.04)`,
          borderColor: alpha(theme.palette.primary.main, 0.3),
        } : undefined,
        ...sx
      }}
      {...props}
    >
      {children}
    </MotionBox>
  );
};